// src/components/HeroDeck.tsx
import React, { useEffect, useRef, useState } from 'react';

type Props = { src: string; cuePoint?: number; title?: string };

export default function HeroDeck({ src, cuePoint = 0, title }: Props) {
  const aRef = useRef<HTMLAudioElement>(null);

  // estado
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [cueHeld, setCueHeld] = useState(false); // pressionando CUE?
  const [cueHot, setCueHot] = useState(false);   // glow visual do CUE

  // espelhos/refs para controle fino
  const cueHeldRef = useRef(false);
  useEffect(() => { cueHeldRef.current = cueHeld; }, [cueHeld]);

  // promoção: se clicar PLAY enquanto segura CUE, ao soltar CUE mantém tocando
  const promoteRef = useRef(false);

  // serialização de ações (evita colisões "play→cue" no mobile)
  const actionLockRef = useRef<Promise<void> | null>(null);
  const withLock = async (fn: () => Promise<void> | void) => {
    if (actionLockRef.current) { try { await actionLockRef.current; } catch { } }
    const p = Promise.resolve().then(() => fn()) as Promise<void>;
    actionLockRef.current = p;
    try { await p; } finally { if (actionLockRef.current === p) actionLockRef.current = null; }
  };

  // token para cancelar vinylStop em andamento
  const stopTokenRef = useRef(0);

  // pointer tracking do CUE + janelas anti-colisão
  const cuePointerIdRef = useRef<number | null>(null);
  const lastPlayAtRef = useRef(0);
  const lastStopAtRef = useRef(0);
  const canCueNow = () => {
    const now = performance.now();
    return now - Math.max(lastPlayAtRef.current, lastStopAtRef.current) > 120; // 120ms
  };

  // RAF para progresso
  const rafRef = useRef<number | null>(null);
  const stopRAF = () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); rafRef.current = null; };
  const loop = () => {
    const a = aRef.current; if (!a) return;
    if (!isFinite(a.duration) || a.duration <= 0) setProgress(0);
    else setProgress(Math.min(1, a.currentTime / a.duration));
    rafRef.current = requestAnimationFrame(loop);
  };

  // pausa suave, cancelável
  const vinylStop = (ms = 380) => new Promise<void>((resolve) => {
    const a = aRef.current; if (!a) return resolve();
    const token = ++stopTokenRef.current;
    const rate0 = a.playbackRate || 1;
    const vol0 = a.volume ?? 1;
    const t0 = performance.now();
    const step = (now: number) => {
      if (token !== stopTokenRef.current) return resolve(); // cancelado por novo gesto
      const t = Math.min(1, (now - t0) / ms);
      const ease = 1 - Math.pow(1 - t, 2);
      a.playbackRate = Math.max(0.05, rate0 * (1 - ease));
      a.volume = vol0 * (1 - ease);
      if (t < 1 && !a.paused) requestAnimationFrame(step);
      else {
        if (token !== stopTokenRef.current) return resolve();
        a.pause();
        a.playbackRate = rate0;
        a.volume = vol0;
        setPlaying(false);
        stopRAF();
        lastStopAtRef.current = performance.now(); // marca quando parou
        resolve();
      }
    };
    requestAnimationFrame(step);
  });

  // PLAY / PAUSE (toggle) — serializado e com promoção do CUE
  const toggle = async () => withLock(async () => {
    const a = aRef.current; if (!a) return;
    try {
      if (a.paused) {
        if (cueHeldRef.current) promoteRef.current = true; // clicou PLAY enquanto segurava CUE
        stopTokenRef.current++; // cancela qualquer stop em curso
        a.muted = false; a.playbackRate = 1; a.volume = 1;
        await a.play();
        lastPlayAtRef.current = performance.now(); // marca quando começou tocar
        setPlaying(true);
        stopRAF(); rafRef.current = requestAnimationFrame(loop);
      } else {
        await vinylStop();
      }
    } catch (err) {
      console.warn('Falha ao dar play:', err);
    }
  });

  // CUE — press & hold: toca do cuePoint enquanto segurando; soltar = parar e voltar
  const handleCueDown = async (e: React.PointerEvent<HTMLButtonElement>) => withLock(async () => {
    const a = aRef.current; if (!a) return;
    if (!canCueNow()) return; // evita colisão logo após play/stop

    // pointer capture — recebemos o pointerup mesmo se o dedo sair do botão
    try { (e.currentTarget as any).setPointerCapture?.(e.pointerId); } catch { }
    cuePointerIdRef.current = e.pointerId;

    stopTokenRef.current++; // cancela vinylStop se estava rolando
    setCueHeld(true); setCueHot(true);
    try { a.currentTime = cuePoint || 0; } catch { }
    a.muted = false; a.playbackRate = 1; a.volume = 1;
    try {
      await a.play();
      setPlaying(true);
      stopRAF(); rafRef.current = requestAnimationFrame(loop);
    } catch (err) {
      console.warn('Cue play bloqueado:', err);
    }
  });

  const handleCueUp = (e?: React.PointerEvent<HTMLButtonElement>) => withLock(async () => {
    // se veio de pointerup, exige o mesmo pointerId
    if (e && cuePointerIdRef.current != null && e.pointerId !== cuePointerIdRef.current) return;
    if (!cueHeldRef.current) return;
    const a = aRef.current; if (!a) return;

    setCueHeld(false); setCueHot(false);
    cuePointerIdRef.current = null;

    // se promoveu (clicou PLAY enquanto segurava CUE), mantém tocando
    if (promoteRef.current) {
      promoteRef.current = false;
      if (!a.paused) { stopRAF(); rafRef.current = requestAnimationFrame(loop); setPlaying(true); }
      return;
    }

    stopTokenRef.current++; // cancela qualquer stop pendente
    a.pause();
    try { a.currentTime = cuePoint || 0; } catch { }
    a.playbackRate = 1; a.volume = 1;
    setPlaying(false); stopRAF(); setProgress(0);
  });

  // listeners do <audio/> para refletir estado
  useEffect(() => {
    const a = aRef.current; if (!a) return;
    const onPlay = () => { setPlaying(true); stopRAF(); rafRef.current = requestAnimationFrame(loop); };
    const onPause = () => { setPlaying(false); stopRAF(); };
    const onEnded = () => { setPlaying(false); stopRAF(); setProgress(1); };
    const onLoaded = () => { setProgress(0); };
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEnded);
    a.addEventListener('loadedmetadata', onLoaded);
    return () => {
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onEnded);
      a.removeEventListener('loadedmetadata', onLoaded);
      try { a.pause(); } catch { }
      stopRAF();
    };
  }, []);

  return (
    <div className="kl-cdj kl-cdj--fixed">
      {/* coluna dos botões (empilhados) */}
      <div className="kl-cdj-stack">
        {/* CUE (topo) — press and hold */}
        <button
          type="button"
          className={`kl-cdj-btn kl-cdj-cue ${cueHot ? 'is-hot' : ''}`}
          aria-label="Cue (pressione para tocar do início)"
          onPointerDown={(e) => { e.preventDefault(); handleCueDown(e); }}
          onPointerUp={(e) => { e.preventDefault(); handleCueUp(e); }}
          onPointerCancel={(e) => { handleCueUp(e); }}
        >
          <span className="kl-cdj-knob">CUE</span>
        </button>

        {/* PLAY (embaixo) com anel de progresso (var --p 0..1) */}
        <button
          type="button"
          className={`kl-cdj-btn kl-cdj-play ${playing ? 'is-on' : ''}`}
          aria-label={playing ? 'Pause' : 'Play'}
          onPointerDown={(e) => { e.preventDefault(); toggle(); }}
          style={{ ['--p' as any]: progress }}
        >
          <span className="kl-cdj-knob">
            {playing ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <rect x="5" y="4" width="5" height="16" rx="1.5" stroke="currentColor" strokeWidth="2" />
                <rect x="14" y="4" width="5" height="16" rx="1.5" stroke="currentColor" strokeWidth="2" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M6 4l14 8-14 8V4z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinejoin="round" />
              </svg>
            )}
          </span>
        </button>
      </div>

      {/* VINIL + “tocando agora” embaixo dos botões */}
      <div className="kl-cdj-bottom">
        <span className={`kl-cdj-vinyl ${playing ? 'is-spin' : ''}`} aria-hidden="true" />
        <div className="kl-cdj-now">
          TOCANDO AGORA: <strong>{title ?? '—'}</strong>
        </div>
      </div>

      {/* elemento de áudio */}
      <audio
        ref={aRef}
        src={src}
        preload="auto"
        playsInline
        onError={(e) => console.error('[AUDIO ERROR]', (e.currentTarget as HTMLAudioElement).src)}
      />
    </div>
  );
}